import React, { useState, useEffect } from "react";
import { userAPI, availabilityAPI, nurseAPI } from "../../services/api";
import axiosInstance from '../../services/axiosInstance';
import { toast } from "react-toastify";

// Skeleton Loader Components
const AvailabilitySkeleton = () => (
  <div className="space-y-6">
    {/* Header Skeleton */}
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <div className="h-6 bg-gray-200 rounded w-48 animate-pulse mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-64 animate-pulse"></div>
      </div>
      <div className="h-10 bg-gray-200 rounded w-40 animate-pulse"></div>
    </div>

    {/* Calendar Grid Skeleton */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Calendar Navigation Skeleton */}
      <div className="relative flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
          <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
        </div>
      </div>

      {/* Calendar Content Skeleton */}
      <div className="p-4">
        {/* Calendar Headers Skeleton */}
        <div className="grid grid-cols-8 gap-1 mb-2">
          <div className="text-center">
            <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
          </div>
          {[...Array(7)].map((_, idx) => (
            <div key={idx} className="text-center">
              <div className="h-8 bg-gray-200 rounded animate-pulse mb-1"></div>
              <div className="h-4 bg-gray-200 rounded w-20 animate-pulse mx-auto"></div>
            </div>
          ))}
        </div>

        {/* Time Slots Skeleton */}
        <div className="space-y-1">
          {[...Array(8)].map((_, rowIdx) => (
            <div key={rowIdx} className="grid grid-cols-8 gap-1">
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              {[...Array(7)].map((_, colIdx) => (
                <div key={colIdx} className="h-10 bg-gray-200 rounded animate-pulse"></div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const SaveButtonSkeleton = () => (
  <div className="flex items-center justify-between">
    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
    <div className="flex items-center gap-3">
      <div className="h-10 w-20 bg-gray-200 rounded animate-pulse"></div>
      <div className="h-10 w-32 bg-gray-200 rounded animate-pulse"></div>
    </div>
  </div>
);

const Settings = () => {
  console.log("Settings component mounted");
  
  // ----- User Info State -----
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [timezone, setTimezone] = useState("");
  const [userId, setUserId] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [imageUpdateCounter, setImageUpdateCounter] = useState(0);

  // New nurse profile state
  const [nurseProfile, setNurseProfile] = useState({
    ahpraNumber: '',
    otherQualifications: '',
    address: '', // Changed from object to string
    phone: '',
    dateOfBirth: '',
    bankDetails: {
      bankName: '',
      bsbNumber: '',
      accountNumber: ''
    },
    abn: '',
    companyName: ''
  });

  // Add state for validation errors
  const [errors, setErrors] = useState({});

  // Set userId from localStorage only once
  useEffect(() => {
    const userString = localStorage.getItem("user");
    if (userString) {
      const user = JSON.parse(userString);
      const localUserId = user.id || user._id || "";
      setUserId(localUserId);
    }
  }, []);

  // Fetch user info when userId is set
  useEffect(() => {
    if (!userId) return;
    console.log("Fetching user info for userId:", userId);
    const fetchUserInfo = async () => {
      try {
        const response = await userAPI.getUserById(userId);
        const user = response.user;
        setName(user.name || "");
        setEmail(user.email || "");
        setProfileImage(user.profileImage || "");
        setTimezone(user.timezone || "Asia/Kolkata");
      } catch (error) {
        console.error("Error fetching user info from API:", error);
      }
    };
    fetchUserInfo();
  }, [userId]);

  // Fetch nurse profile
  useEffect(() => {
    const fetchNurseProfile = async () => {
      if (!userId) return;
      try {
        const response = await nurseAPI.getNurseProfile();
        if (response.success) {
          setNurseProfile(response.nurseProfile);
        }
      } catch (error) {
        console.error('Error fetching nurse profile:', error);
        // toast.error('Failed to load nurse profile');
      }
    };
    fetchNurseProfile();
  }, [userId]);

  // Helper function to get correct image URL
  const getProfileImageUrl = (imagePath) => {
    console.log('getProfileImageUrl called with:', imagePath);
    if (!imagePath) return "https://cdn-icons-png.flaticon.com/512/149/149071.png";

    // If it's already a full URL, return as is
    if (imagePath.startsWith('https')) {
      console.log('Returning full URL as is:', imagePath);
      return imagePath;
    }

    // Use the same domain as the API but without /api
    const baseUrl = 'https://backend.adhdrapidcare.com';

    // If it's a relative path, add the base URL
    if (imagePath.startsWith('/')) {
      const fullUrl = `${baseUrl}${imagePath}`;
      console.log('Constructed full URL from relative path:', fullUrl);
      return fullUrl;
    }

    // If it doesn't start with /, add it
    const fullUrl = `${baseUrl}/${imagePath}`;
    console.log('Constructed full URL from path without slash:', fullUrl);
    return fullUrl;
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'All fields are required' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
      return;
    }

    setChangingPassword(true);
    setPasswordMessage({ type: '', text: '' });

    try {
      const response = await axiosInstance.put(`/users/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.data) {
        setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordMessage({ type: '', text: '' });
        }, 2000);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordMessage({
        type: 'error',
        text: 'Failed to change password: ' + (error.response?.data?.message || error.message || 'Unknown error')
      });
    } finally {
      setChangingPassword(false);
    }
  };

  // Handle nurse profile changes
  const handleNurseProfileChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setNurseProfile(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setNurseProfile(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Add qualifications
  const [newQualification, setNewQualification] = useState('');
  const handleAddQualification = () => {
    if (newQualification.trim()) {
      setNurseProfile(prev => ({
        ...prev,
        otherQualifications: [...prev.otherQualifications, newQualification.trim()]
      }));
      setNewQualification('');
    }
  };

  // Remove qualification
  const handleRemoveQualification = (index) => {
    setNurseProfile(prev => ({
      ...prev,
      otherQualifications: prev.otherQualifications.filter((_, i) => i !== index)
    }));
  };

     // ----- Availability State -----
   const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
   const [availability, setAvailability] = useState(
     days.map(() => ({
       enabled: false,
       from: "10:00 AM",
       to: "6:00 PM",
     }))
   );
   // New state for date-based availability
   const [dateAvailability, setDateAvailability] = useState([]);
   const [slotDuration, setSlotDuration] = useState(30);
   const [breakDuration, setBreakDuration] = useState(10);
   const [loadingAvailability, setLoadingAvailability] = useState(true);
   const [savingAvailability, setSavingAvailability] = useState(false);
   const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });

  // Fetch availability data
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!userId) return;

      try {
        setLoadingAvailability(true);
        console.log('Fetching availability for user:', userId);

        const response = await availabilityAPI.getAvailabilityByUserId(userId);
        console.log('Availability response:', response);

        if (response.message && response.availability) {
          const data = response.availability;

          console.log('Raw availability data from API:', data);
          console.log('Slot duration from API:', data.slotDuration);
          console.log('Break duration from API:', data.breakDuration);

          // Update timezone
          setTimezone(data.timezone || "Asia/Kolkata");

          // Update slot and break duration
          setSlotDuration(data.slotDuration || 30);
          setBreakDuration(data.breakDuration || 10);

          // Update session duration from API (convert slotDuration to sessionDuration format)
          const sessionDurationValue = data.slotDuration || 30;
          console.log('Setting session duration from API:', sessionDurationValue, 'minutes');
          setSessionDuration(`${sessionDurationValue} minutes`);

          // Update availability for each day
          const updatedAvailability = days.map(day => {
            const dayData = data.availability && data.availability[day];
            console.log(`Processing ${day}:`, dayData);

            if (dayData && dayData.start && dayData.end) {
              // Convert time format if needed
              const startTime = formatTimeForAPI(dayData.start);
              const endTime = formatTimeForAPI(dayData.end);

              console.log(`${day} - Start: ${dayData.start} -> ${startTime}, End: ${dayData.end} -> ${endTime}`);

              return {
                enabled: true,
                from: startTime,
                to: endTime,
              };
            } else {
              console.log(`${day} - No data found, using defaults`);
              return {
                enabled: false,
                from: "9:00 AM",
                to: "5:00 PM",
              };
            }
          });

          setAvailability(updatedAvailability);
          console.log('Updated availability state:', updatedAvailability);

          // Also fetch date-based availability
          try {
            const dateResponse = await availabilityAPI.getDateAvailability(userId);
            console.log('Raw date response:', dateResponse);
            if (dateResponse.message && dateResponse.availability) {
              // The API returns availability as a direct array, not nested under dateAvailability
              setDateAvailability(dateResponse.availability || []);
              console.log('Date availability loaded:', dateResponse.availability);
            }
          } catch (dateError) {
            console.log('No date availability found or error:', dateError);
            setDateAvailability([]);
          }
        } else {
          console.log('No availability data found, using defaults');
          // Keep default values if no data found
        }
      } catch (error) {
        console.error("Error fetching availability:", error);
        console.error("Error details:", error.response?.data || error.message);

        // Handle specific error cases
        if (error.response?.status === 404) {
          console.log('No availability found for user, using default values');
          // This is normal for new users who haven't set availability yet
        } else {
          console.error("Unexpected error fetching availability");
        }

        // Keep default values if fetch fails
      } finally {
        setLoadingAvailability(false);
      }
    };

    const fetchSessionPreferences = async () => {
      if (!userId) return;

      try {
        console.log('Fetching toggle preferences for user:', userId);

        // Load toggle preferences from localStorage only
        const savedTogglePreferences = localStorage.getItem(`togglePreferences_${userId}`);
        if (savedTogglePreferences) {
          const preferences = JSON.parse(savedTogglePreferences);
          console.log('Loaded toggle preferences from localStorage:', preferences);
          setAutoJoin(preferences.autoJoin || false);
          setNotesPopup(preferences.notesPopup || false);
          setManualApproval(preferences.manualApproval || false);
          setEmailReminders(preferences.emailReminders !== undefined ? preferences.emailReminders : true);
          setPostSessionEmails(preferences.postSessionEmails !== undefined ? preferences.postSessionEmails : true);
        } else {
          console.log('No toggle preferences found in localStorage, using defaults');
          // Keep default values if no data found
        }
      } catch (error) {
        console.error("Error loading toggle preferences:", error);
        // Keep default values if load fails
      }
    };

    fetchAvailability();
    fetchSessionPreferences();
  }, [userId]);

  const handleToggle = (index) => {
    const updated = [...availability];
    updated[index].enabled = !updated[index].enabled;
    setAvailability(updated);
  };

  const handleTimeChange = (index, key, value) => {
    const updated = [...availability];
    updated[index][key] = value;
    setAvailability(updated);
  };

const timeOptions = [
  "7:00 AM", "7:10 AM", "7:20 AM", "7:30 AM", "7:40 AM", "7:50 AM",
  "8:00 AM", "8:10 AM", "8:20 AM", "8:30 AM", "8:40 AM", "8:50 AM",
  "9:00 AM", "9:10 AM", "9:20 AM", "9:30 AM", "9:40 AM", "9:50 AM",
  "10:00 AM", "10:10 AM", "10:20 AM", "10:30 AM", "10:40 AM", "10:50 AM",
  "11:00 AM", "11:10 AM", "11:20 AM", "11:30 AM", "11:40 AM", "11:50 AM",
  "12:00 PM", "12:10 PM", "12:20 PM", "12:30 PM", "12:40 PM", "12:50 PM",
  "1:00 PM", "1:10 PM", "1:20 PM", "1:30 PM", "1:40 PM", "1:50 PM",
  "2:00 PM", "2:10 PM", "2:20 PM", "2:30 PM", "2:40 PM", "2:50 PM",
  "3:00 PM", "3:10 PM", "3:20 PM", "3:30 PM", "3:40 PM", "3:50 PM",
  "4:00 PM", "4:10 PM", "4:20 PM", "4:30 PM", "4:40 PM", "4:50 PM",
  "5:00 PM", "5:10 PM", "5:20 PM", "5:30 PM", "5:40 PM", "5:50 PM",
  "6:00 PM", "6:10 PM", "6:20 PM", "6:30 PM", "6:40 PM", "6:50 PM",
  "7:00 PM"
];

  // Helper function to format time to HH:MM AM/PM format
  const formatTimeForAPI = (timeString) => {
    if (timeString.match(/^\d{1,2}:\d{2}\s(AM|PM)$/)) {
      return timeString;
    }

    if (timeString.match(/^\d{2}:\d{2}$/)) {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour.toString().padStart(2, '0')}:${minutes} ${ampm}`;
    }

    if (timeString.match(/^\d{1,2}:\d{2}\s(AM|PM)$/)) {
      const [time, ampm] = timeString.split(' ');
      const [hours, minutes] = time.split(':');
      return `${hours.padStart(2, '0')}:${minutes} ${ampm}`;
    }

    return timeString;
  };

  // Save all settings including nurse profile
  const saveAllSettings = async () => {
    try {
      setSavingAvailability(true);
      setSaveMessage({ type: '', text: '' });
      setErrors({}); // Clear previous errors

      // Save nurse profile
      try {
        const nurseProfileResponse = await nurseAPI.updateNurseProfile(nurseProfile);
        if (!nurseProfileResponse.success) {
          throw new Error(nurseProfileResponse.message || 'Failed to save nurse profile');
        }
      } catch (error) {
        console.error('Error saving nurse profile:', error);
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        }
        setSaveMessage({
          type: 'error',
          text: 'Failed to save nurse profile: ' + (error.response?.data?.message || error.message)
        });
        return;
      }

      // Save user profile
      if (userId) {
        try {
          let profileData;
          if (selectedImage) {
            profileData = new FormData();
            profileData.append('name', name);
            profileData.append('email', email);
            profileData.append('timezone', timezone);
            profileData.append('profileImage', selectedImage);
          } else {
            profileData = {
              name,
              email,
              timezone
            };
          }
          const profileResponse = await axiosInstance.put(`/users/${userId}`, profileData, selectedImage ? {
            headers: { 'Content-Type': 'multipart/form-data' },
          } : {});

          console.log('Profile update response:', profileResponse);

          if (profileResponse.data) {
            const userString = localStorage.getItem("user");
            if (userString) {
              const user = JSON.parse(userString);
              const updatedUser = {
                ...user,
                name: name,
                email: email,
                timezone: timezone,
                profileImage: profileResponse.data.user?.profileImage || profileResponse.data.profileImage || user.profileImage
              };
              localStorage.setItem("user", JSON.stringify(updatedUser));
            }

            let newImageUrl = null;
            if (profileResponse.data.user && profileResponse.data.user.profileImage) {
              newImageUrl = profileResponse.data.user.profileImage;
            } else if (profileResponse.data.profileImage) {
              newImageUrl = profileResponse.data.profileImage;
            }

            // Update local state directly after save
            setName(profileResponse.data.user?.name || name);
            setProfileImage(newImageUrl || profileImage);
            setTimezone(profileResponse.data.user?.timezone || timezone);

            // Clear image preview and selected image
            setImagePreview("");
            setSelectedImage(null);

            // Force re-render by incrementing counter
            setImageUpdateCounter(prev => prev + 1);

            setSelectedImage(null);
            setImagePreview("");

            console.log('Profile update successful');
          }
        } catch (error) {
          console.error('Error updating profile:', error);
          setSaveMessage({
            type: 'error',
            text: 'Failed to update profile: ' + (error.response?.data?.message || error.message || 'Unknown error')
          });
          return;
        }
      }

      // Build the availability object
      const availabilityData = {};
      days.forEach((day, index) => {
        if (availability[index].enabled) {
          const startTime = formatTimeForAPI(availability[index].from);
          const endTime = formatTimeForAPI(availability[index].to);
          availabilityData[day] = {
            start: startTime,
            end: endTime
          };
        }
      });

      const availabilityPayload = {
        userId: userId,
        timezone: timezone,
        slotDuration: parseInt(sessionDuration.split(' ')[0]), // Convert "60 minutes" to 60
        breakDuration: breakDuration,
        availability: availabilityData
      };

      const togglePreferencesPayload = {
        userId: userId,
        autoJoin: autoJoin,
        notesPopup: notesPopup,
        manualApproval: manualApproval,
        emailReminders: emailReminders,
        postSessionEmails: postSessionEmails
      };

      console.log('Saving availability with payload:', availabilityPayload);
      console.log('Saving toggle preferences to localStorage:', togglePreferencesPayload);

      // Save availability, session duration, and buffer time to API
      const availabilityResponse = await availabilityAPI.createDoctorAvailability(availabilityPayload);

      // Save toggle preferences to localStorage only
      localStorage.setItem(`togglePreferences_${userId}`, JSON.stringify(togglePreferencesPayload));

      if (availabilityResponse.message) {
        setSaveMessage({ type: 'success', text: 'All settings saved successfully!' });
        setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
      } else {
        setSaveMessage({ type: 'error', text: 'Failed to save settings: ' + (availabilityResponse.message || 'Unknown error') });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage({
        type: 'error',
        text: 'Error saving settings: ' + (error.response?.data?.message || error.message || 'Unknown error')
      });
    } finally {
      setSavingAvailability(false);
    }
  };

  // Keep the old function name for backward compatibility
  const saveAvailability = saveAllSettings;

  const [sessionDuration, setSessionDuration] = useState("30 minutes");
  const [autoJoin, setAutoJoin] = useState(false);
  const [notesPopup, setNotesPopup] = useState(false);
  const [manualApproval, setManualApproval] = useState(false);
  const [emailReminders, setEmailReminders] = useState(true);
  const [postSessionEmails, setPostSessionEmails] = useState(true);
  const [bufferTime, setBufferTime] = useState("10 minutes");

  useEffect(() => {
    console.log('Session duration changed to:', sessionDuration);
  }, [sessionDuration]);

     // Calendar Component for Availability Settings
   const AvailabilityCalendar = ({ availability, onAvailabilityChange, onDateAvailabilityChange, slotDuration, onSlotDurationChange }) => {
         const [selectedDate, setSelectedDate] = useState(() => {
           // Ensure we start exactly from September 15th, 2025
           const september15 = new Date('2025-09-15');
           // Set to the start of the week containing September 15th
           const day = september15.getDay();
           const diff = september15.getDate() - day + (day === 0 ? -6 : 1);
           const startOfWeek = new Date(september15);
           startOfWeek.setDate(diff);
           return startOfWeek;
         }); // Set to show the week starting from September 15th
    const [showAddModal, setShowAddModal] = useState(false);
         const [selectedTimeSlot, setSelectedTimeSlot] = useState({
       startTime: '10:00 AM',
       endTime: '11:00 AM',
       date: new Date('2025-09-15') // Start from September 15th, 2025
     });
    const [saving, setSaving] = useState(false);

    // Generate time slots for the modal
    const timeOptions = [
     
     "7:00 AM", "7:10 AM", "7:20 AM", "7:30 AM", "7:40 AM", "7:50 AM",
     "8:00 AM", "8:10 AM", "8:20 AM", "8:30 AM", "8:40 AM", "8:50 AM",
     "9:00 AM", "9:10 AM", "9:20 AM", "9:30 AM", "9:40 AM", "9:50 AM",
     "10:00 AM", "10:10 AM", "10:20 AM", "10:30 AM", "10:40 AM", "10:50 AM",
"11:00 AM", "11:10 AM", "11:20 AM", "11:30 AM", "11:40 AM", "11:50 AM",
"12:00 PM", "12:10 PM", "12:20 PM", "12:30 PM", "12:40 PM", "12:50 PM",
"1:00 PM", "1:10 PM", "1:20 PM", "1:30 PM", "1:40 PM", "1:50 PM",
"2:00 PM", "2:10 PM", "2:20 PM", "2:30 PM", "2:40 PM", "2:50 PM",
"3:00 PM", "3:10 PM", "3:20 PM", "3:30 PM", "3:40 PM", "3:50 PM",
"4:00 PM", "4:10 PM", "4:20 PM", "4:30 PM", "4:40 PM", "4:50 PM",
"5:00 PM", "5:10 PM", "5:20 PM", "5:30 PM", "5:40 PM", "5:50 PM",
"6:00 PM", "6:10 PM", "6:20 PM", "6:30 PM", "6:40 PM", "6:50 PM",
"7:00 PM"

    ];

    // Get current week dates - ensure we start from September 15th onwards
    const getWeekDates = () => {
      const startOfWeek = new Date(selectedDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      
      // Ensure we don't show weeks before September 15th
      const september15 = new Date('2025-09-15');
      if (startOfWeek < september15) {
        startOfWeek.setTime(september15.getTime());
      }
      
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        
        // Only add dates that are September 15th or later
        if (date >= september15) {
          dates.push(date);
        }
      }
      
      // If we don't have 7 dates, fill the remaining slots with future dates
      while (dates.length < 7) {
        const lastDate = dates[dates.length - 1];
        const nextDate = new Date(lastDate);
        nextDate.setDate(lastDate.getDate() + 1);
        dates.push(nextDate);
      }
      
      return dates;
    };

    const weekDates = getWeekDates();
    
    // Debug: Log the week dates being displayed
    console.log('Calendar displaying week dates:', weekDates.map(d => d.toISOString().split('T')[0]));

    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    };

    const isToday = (date) => {
      const today = new Date();
      return date.toDateString() === today.toDateString();
    };

    const navigateWeek = (direction) => {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() + (direction * 7));
      
      // Prevent navigation to weeks before September 15th
      const minDate = new Date('2025-09-15');
      if (newDate < minDate) {
        return; // Don't allow navigation to earlier weeks
      }
      
      setSelectedDate(newDate);
    };

    const handleAddAvailability = () => {
      setSelectedTimeSlot({
        date: new Date('2025-09-15'), // Start from September 15th, 2025
        timeSlots: [
          {
            startTime: '7:00 AM',
            endTime: '8:00 AM',
            enabled: true
          }
        ]
      });
      setShowAddModal(true);
    };

    // Helper function to get day name from date
    const getDayName = (date) => {
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      return days[date.getDay()];
    };

    // Helper function to format time for API
    const formatTimeForAPI = (timeString) => {
      if (timeString.match(/^\d{1,2}:\d{2}\s(AM|PM)$/)) {
        return timeString;
      }

      if (timeString.match(/^\d{2}:\d{2}$/)) {
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour.toString().padStart(2, '0')}:${minutes} ${ampm}`;
      }

      return timeString;
    };

         const handleSaveAvailability = async () => {
       try {
         setSaving(true);
         
         // Check if selected date is in the past
         const today = new Date();
         today.setHours(0, 0, 0, 0); // Reset time to start of day
         
         if (selectedTimeSlot.date < today) {
           toast.error('Cannot select dates in the past. Please choose a date from today onwards.');
           setSaving(false);
           return;
         }
         
         // Validate all time slots
         for (let i = 0; i < selectedTimeSlot.timeSlots.length; i++) {
           const slot = selectedTimeSlot.timeSlots[i];
           if (convertTimeToMinutes(slot.endTime) <= convertTimeToMinutes(slot.startTime)) {
             toast.error(`Time slot ${i + 1}: End time must be after start time`);
             setSaving(false);
             return;
           }
         }
         
         // Format the date to YYYY-MM-DD
         const dateString = selectedTimeSlot.date.toISOString().split('T')[0];
         
         // Call the new date-based availability API with multiple time slots
         const response = await availabilityAPI.addDateAvailability({
           userId: userId,
           date: dateString,
           timeSlots: selectedTimeSlot.timeSlots,
           enabled: true
         });

        if (response.message) {
          // Show success with time slots info
          const timeSlotsText = selectedTimeSlot.timeSlots.map(slot => 
            `${slot.startTime}-${slot.endTime}`
          ).join(', ');
          toast.success(`Availability added successfully for ${dateString}: ${timeSlotsText}!`);
          
          // Complete page refresh after successful addition
          window.location.reload();
        } else {
          toast.error('Failed to save availability');
        }
        
      } catch (error) {
        console.error('Error saving availability:', error);
        toast.error('Failed to save availability: ' + (error.response?.data?.message || error.message));
      } finally {
        setSaving(false);
      }
    };



    // Check if a specific time slot is available for a specific date (SIMPLIFIED)
    const isTimeSlotAvailable = (date, time) => {
      // Format the date to match the database format
      const dateString = date.toISOString().split('T')[0];
      
      // Check if we have date-based availability for this specific date
      if (availability.dateAvailability && Array.isArray(availability.dateAvailability)) {
        const dateAvailability = availability.dateAvailability.find(
          da => {
            if (!da.date) return false;
            // Handle both string and Date object formats
            const dbDate = typeof da.date === 'string' ? da.date.split('T')[0] : da.date.toISOString().split('T')[0];
            return dbDate === dateString;
          }
        );
        
        if (dateAvailability && dateAvailability.enabled && dateAvailability.timeSlots && Array.isArray(dateAvailability.timeSlots)) {
          // Check if the time falls within any of the time slots for this date
          for (const timeSlot of dateAvailability.timeSlots) {
            if (timeSlot.enabled && timeSlot.startTime && timeSlot.endTime) {
              const timeInMinutes = convertTimeToMinutes(time);
              const startTimeInMinutes = convertTimeToMinutes(timeSlot.startTime);
              const endTimeInMinutes = convertTimeToMinutes(timeSlot.endTime);
              
              const isAvailable = timeInMinutes >= startTimeInMinutes && timeInMinutes <= endTimeInMinutes;
              if (isAvailable) {
                return true;
              }
            }
          }
          return false;
        }
      }
      
      // Fallback to weekly availability for backward compatibility
      const dayName = getDayName(date);
      const dayIndex = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].indexOf(dayName);
      
      if (dayIndex === -1 || !availability[dayIndex]?.enabled) {
        return false;
      }

      const dayAvailability = availability[dayIndex];
      const timeInMinutes = convertTimeToMinutes(time);
      const startTimeInMinutes = convertTimeToMinutes(dayAvailability.from);
      const endTimeInMinutes = convertTimeToMinutes(dayAvailability.to);

      // Check if the time slot falls within the availability range
      return timeInMinutes >= startTimeInMinutes && timeInMinutes <= endTimeInMinutes;
    };

         // Helper function to convert time to minutes for comparison
     const convertTimeToMinutes = (timeString) => {
       if (!timeString) return 0;
       
       const [time, period] = timeString.split(' ');
       let [hours, minutes] = time.split(':').map(Number);
       
       if (period === 'PM' && hours !== 12) {
         hours += 12;
       } else if (period === 'AM' && hours === 12) {
         hours = 0;
       }
       
       return hours * 60 + minutes;
     };

     // Helper function to get the next hour for a given time
     const getNextHour = (timeString) => {
       const [time, period] = timeString.split(' ');
       let [hours, minutes] = time.split(':').map(Number);
       
       // Add 1 hour
       hours += 1;
       
       // Handle AM/PM transitions
       if (hours === 12) {
         // 12 AM becomes 12 PM, 12 PM becomes 1 PM
         if (period === 'AM') {
           return '12:00 PM';
         } else {
           return '1:00 PM';
         }
       } else if (hours === 13) {
         // 1 PM becomes 2 PM
         return '2:00 PM';
       } else if (hours > 12) {
         // Convert to 12-hour format
         const displayHour = hours - 12;
         return `${displayHour}:00 PM`;
       } else {
         // Keep AM format
         return `${hours}:00 AM`;
       }
     };

     // Helper function to get valid end time options based on start time
     const getValidEndTimeOptions = (startTime) => {
       const startMinutes = convertTimeToMinutes(startTime);
       return timeOptions.filter(time => convertTimeToMinutes(time) > startMinutes);
     };

    // Get availability info for a specific date to display in the calendar (SIMPLIFIED)
    const getAvailabilityInfo = (date) => {
      // Format the date to match the database format
      const dateString = date.toISOString().split('T')[0];
      
      // Debug logging
      console.log('Getting availability info for date:', dateString);
      console.log('Current availability data:', availability);
      
      // Check if we have date-based availability for this specific date
      if (availability.dateAvailability && Array.isArray(availability.dateAvailability)) {
        console.log('Date availability array:', availability.dateAvailability);
        
        const dateAvailability = availability.dateAvailability.find(
          da => {
            if (!da.date) return false;
            // Handle both string and Date object formats
            const dbDate = typeof da.date === 'string' ? da.date.split('T')[0] : da.date.toISOString().split('T')[0];
            console.log('Comparing dates:', dbDate, '===', dateString, 'Result:', dbDate === dateString);
            return dbDate === dateString;
          }
        );
        
        console.log('Found date availability for info:', dateAvailability);
        
        if (dateAvailability && dateAvailability.enabled && dateAvailability.timeSlots && Array.isArray(dateAvailability.timeSlots)) {
          // Return the first time slot for display (or combine multiple slots)
          const enabledSlots = dateAvailability.timeSlots.filter(slot => slot.enabled);
          if (enabledSlots.length > 0) {
            if (enabledSlots.length === 1) {
              const result = {
                from: enabledSlots[0].startTime,
                to: enabledSlots[0].endTime
              };
              console.log('Returning availability info:', result);
              return result;
            } else {
              // Multiple slots - show range
              const result = {
                from: enabledSlots[0].startTime,
                to: enabledSlots[enabledSlots.length - 1].endTime,
                multiple: true,
                count: enabledSlots.length
              };
              console.log('Returning availability info for multiple slots:', result);
              return result;
            }
          }
        }
      }
      
      // Fallback to weekly availability for backward compatibility
      const dayIndex = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].indexOf(getDayName(date));
      
      if (dayIndex === -1 || !availability[dayIndex]?.enabled) {
        return null;
      }

      return availability[dayIndex];
    };

    return (
      <div className="space-y-6">
        {/* Calendar Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Weekly Availability</h3>
            <p className="text-gray-600">Set your availability for the next 2 weeks</p>
          </div>
          
          {/* Add Availability Button */}
          <button
            onClick={handleAddAvailability}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Availability
          </button>
        </div>



        {/* Calendar Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="relative flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            {/* Week Navigation */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateWeek(-1)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-full transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <h4 className="text-lg font-semibold text-gray-800">
                {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
              </h4>
              
              <button
                onClick={() => navigateWeek(1)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-full transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

                     {/* Calendar Grid */}
           <div className="p-4">
             {/* Calendar Headers */}
             <div className="grid grid-cols-8 gap-1 mb-2">
               {/* Time Column Header */}
               <div className="text-center">
                 <div className="text-sm font-medium p-2 rounded bg-blue-100 text-blue-700 border border-blue-200">
                   Time
                 </div>
               </div>
               {/* Day Headers */}
                     {weekDates.map((date) => {
                       const availabilityInfo = getAvailabilityInfo(date);
                       const dateString = date.toISOString().split('T')[0];
                       const hasAvailability = availability.dateAvailability && 
                         availability.dateAvailability.some(da => da.date === dateString && da.enabled);
                       
                       return (
                         <div key={date.toISOString()} className="text-center">
                           <div className={`text-sm font-medium p-2 rounded ${
                             isToday(date) ? 'bg-blue-100 text-blue-600' : 'text-gray-600'
                           }`}>
                             {formatDate(date)}
                           </div>
                           {availabilityInfo && (
                             <div className="text-xs text-green-600 mt-1 font-medium">
                               {availabilityInfo.multiple ? (
                                 <span>
                                   {availabilityInfo.from} - {availabilityInfo.to}
                                   <br />
                                   <span className="text-blue-600">({availabilityInfo.count} slots)</span>
                                 </span>
                               ) : (
                                 `${availabilityInfo.from} - ${availabilityInfo.to}`
                               )}
                             </div>
                           )}
                           {hasAvailability && (
                             <div className="mt-1">
                               <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                 <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                                 Available
                               </span>
                             </div>
                           )}
                         </div>
                       );
                     })}
             </div>

             {/* Time Slots */}
             <div className="space-y-1">
               {timeOptions.map((time) => (
                 <div key={time} className="grid grid-cols-8 gap-1">
                   {/* Time Label Column */}
                   <div className="text-sm text-gray-700 p-2 border-r border-gray-200 flex items-center justify-center font-medium bg-gradient-to-b from-blue-50 to-indigo-100 border border-blue-200">
                     {time}
                   </div>
                   {/* Day Columns */}
                   {weekDates.map((date) => {
                     const isAvailable = isTimeSlotAvailable(date, time);
                     
                     return (
                       <div 
                         key={`${date.toISOString()}-${time}`} 
                         className="p-2 border border-gray-100 min-h-[40px] flex items-center justify-center"
                       >
                         {isAvailable && (
                           <div className="w-full h-full bg-green-100 border border-green-300 rounded-md flex items-center justify-center">
                             <span className="text-xs font-medium text-green-700">Available</span>
                           </div>
                         )}
                       </div>
                     );
                   })}
                 </div>
               ))}
             </div>
           </div>
        </div>

        {/* Current Availability List */}


        {/* Add Availability Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 overflow-y-auto" style={{maxHeight: '80vh'}}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Add Availability</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                                 {/* Date Selection */}
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                     <span>Select Date</span>
                    
                   </label>
                                                                           <input
                      type="date"
                      value={selectedTimeSlot.date.toISOString().split('T')[0]}
                      min="2025-09-15"
                      onChange={(e) => {
                        const selectedDate = new Date(e.target.value);
                        const september15 = new Date('2025-09-15');
                        
                        // Only allow dates from September 15th onwards
                        if (selectedDate >= september15) {
                          setSelectedTimeSlot({
                            ...selectedTimeSlot,
                            date: selectedDate
                          });
                        } else {
                          // Reset to September 15th if earlier date is somehow selected
                          setSelectedTimeSlot({
                            ...selectedTimeSlot,
                            date: september15
                          });
                        }
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      style={{
                        colorScheme: 'light',
                        backgroundColor: '#f9fafb',
                        cursor: 'pointer'
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Only dates from September 15th, 2025 onwards are available
                    </p>
                  {/* <p className="text-xs text-gray-500 mt-1">
                    Selected: {getDayName(selectedTimeSlot.date)}
                  </p> */}
                </div>

                {/* Time Slots */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time Slots
                  </label>
                  <div className="space-y-3">
                    {selectedTimeSlot.timeSlots.map((slot, index) => (
                      <div key={index} className="flex items-center space-x-2 p-3 border border-gray-200 rounded-md bg-gray-50">
                        <div className="flex-1">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                              <select
                                value={slot.startTime}
                                                                 onChange={(e) => {
                                   const newTimeSlots = [...selectedTimeSlot.timeSlots];
                                   const newStartTime = e.target.value;
                                   newTimeSlots[index].startTime = newStartTime;
                                   
                                   // Validate that end time is not less than start time
                                   const endTime = newTimeSlots[index].endTime;
                                   if (endTime && convertTimeToMinutes(newStartTime) >= convertTimeToMinutes(endTime)) {
                                     // If start time is greater than or equal to end time, adjust end time
                                     const nextHour = getNextHour(newStartTime);
                                     newTimeSlots[index].endTime = nextHour;
                                     
                                     
                                   }
                                   
                                   setSelectedTimeSlot({
                                     ...selectedTimeSlot,
                                     timeSlots: newTimeSlots
                                   });
                                 }}
                                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              >
                                {timeOptions.map((time) => (
                                  <option key={time} value={time}>{time}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">End Time</label>
                              <select
                                value={slot.endTime}
                                onChange={(e) => {
                                  const newTimeSlots = [...selectedTimeSlot.timeSlots];
                                  newTimeSlots[index].endTime = e.target.value;
                                  setSelectedTimeSlot({
                                    ...selectedTimeSlot,
                                    timeSlots: newTimeSlots
                                  });
                                }}
                                className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              >
                                {getValidEndTimeOptions(slot.startTime).map((time) => (
                                  <option key={time} value={time}>{time}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedTimeSlot.timeSlots.length > 1) {
                              const newTimeSlots = selectedTimeSlot.timeSlots.filter((_, i) => i !== index);
                              setSelectedTimeSlot({
                                ...selectedTimeSlot,
                                timeSlots: newTimeSlots
                              });
                            }
                          }}
                          className="text-red-500 hover:text-red-700 p-1 mt-5"
                          disabled={selectedTimeSlot.timeSlots.length === 1}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  
                                     {/* Add New Time Slot Button */}
                   <button
                     type="button"
                     onClick={() => {
                       // Get the last time slot to set a valid range
                       const lastSlot = selectedTimeSlot.timeSlots[selectedTimeSlot.timeSlots.length - 1];
                       let newStartTime = '9:00 AM';
                       let newEndTime = '10:00 AM';
                       
                       if (lastSlot && lastSlot.endTime) {
                         // Set start time to the end time of the last slot
                         newStartTime = lastSlot.endTime;
                         // Set end time to 1 hour after start time
                         newEndTime = getNextHour(newStartTime);
                       }
                       
                      const newTimeSlots = [...selectedTimeSlot.timeSlots, {
                        startTime: newStartTime,
                        endTime: newEndTime,
                        enabled: true
                      }];
                      setSelectedTimeSlot({
                        ...selectedTimeSlot,
                        timeSlots: newTimeSlots
                      });
                    }}
                    className="mt-2 w-full px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    + Add Another Time Slot
                  </button>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAvailability}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Add Availability'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-10">
      {/* Account Info */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        {/* Header */}
        <h2 className="text-lg font-semibold text-gray-700 mb-6 flex items-center gap-2">
          <img
            src="/images/SVG (8).svg"
            alt="Account Icon"
            className="w-5 h-5 object-contain"
          />
          Account Information
        </h2>

        <div className="flex flex-col sm:flex-row items-stretch gap-6">
          {/* Left column: Profile image and Upload icon */}
          <div className="flex flex-col items-center sm:w-28 flex-shrink-0">
            <img
              src={imagePreview || (profileImage ? `${getProfileImageUrl(profileImage)}?v=${imageUpdateCounter}` : "https://cdn-icons-png.flaticon.com/512/149/149071.png")}
              alt="Profile"
              className="w-28 h-28 rounded-full object-cover border-2 border-gray-100"
              key={`${profileImage || 'default'}-${imageUpdateCounter}`}
              onError={(e) => {
                e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
              }}
            />

            <label className="cursor-pointer mt-1 ml-16">
              <input type="file" className="hidden" onChange={handleImageChange} accept="image/*" />
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white hover:bg-blue-600 transition">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  ></path>
                </svg>
              </div>
            </label>
            {selectedImage && (
              <p className="text-xs text-blue-600 mt-1">Image selected</p>
            )}
          </div>

          {/* Right column: Form Fields */}
          <div className="flex-1 w-full flex flex-col">
            {/* Full Name */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 font-medium mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Email Address */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 font-medium mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={email || ""}
                readOnly
                className="w-full border border-gray-200 bg-gray-100 text-gray-500 rounded-md px-3 py-2 text-sm focus:ring-0 focus:border-gray-300 cursor-not-allowed"
                placeholder="Enter your email address"
              />
            </div>

            {/* Timezone */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 font-medium mb-1">
                Timezone
              </label>
              <div className="relative w-full">
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="appearance-none w-full border border-gray-300 rounded-md px-4 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 pr-10"
                >
                  <option value="Australia/Sydney">New South Wales</option>
                  <option value="Australia/Darwin">Northern Territory</option>
                  <option value="Australia/Brisbane">Queensland</option>
                  <option value="Australia/Adelaide">South Australia</option>
                  <option value="Australia/Hobart">Tasmania</option>
                  <option value="Australia/Melbourne">Victoria</option>
                  <option value="Australia/Perth">Western Australia</option>
                </select>
                {/* Custom Dropdown Icon */}
                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Change Password Button */}
            <div className="mt-4">
              <button 
                onClick={() => setShowPasswordModal(true)}
                className="bg-white text-blue-600 font-medium px-4 py-2 rounded-md border border-blue-500 hover:bg-blue-50 transition text-sm"
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Nurse Profile Details */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-700 mb-6">Nurse Profile Details</h2>
        
        <div className="space-y-6">
          {/* AHPRA Registration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              AHPRA Registration Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={nurseProfile.ahpraNumber}
              onChange={(e) => handleNurseProfileChange('ahpraNumber', e.target.value)}
              className={`w-full border ${errors.ahpraNumber ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm`}
              placeholder="Enter AHPRA number"
              required
            />
            {errors.ahpraNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.ahpraNumber}</p>
            )}
          </div>

          {/* Other Qualifications */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Other Qualifications
            </label>
            <textarea
              value={nurseProfile.otherQualifications}
              onChange={(e) => handleNurseProfileChange('otherQualifications', e.target.value)}
              className={`w-full border ${errors.otherQualifications ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm`}
              placeholder="Enter other qualifications (optional)"
              rows="3"
            />
            {errors.otherQualifications && (
              <p className="mt-1 text-sm text-red-600">{errors.otherQualifications}</p>
            )}
          </div>

          {/* Home Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Home Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={nurseProfile.address}
              onChange={(e) => handleNurseProfileChange('address', e.target.value)}
              className={`w-full border ${errors.address ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm`}
              placeholder="Enter full home address"
              required
            />
            {errors.address && (
              <p className="mt-1 text-sm text-red-600">{errors.address}</p>
            )}
          </div>

          {/* Mobile Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={nurseProfile.phone}
              onChange={(e) => handleNurseProfileChange('phone', e.target.value)}
              className={`w-full border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm`}
              placeholder="Enter mobile number"
              required
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
            )}
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={nurseProfile.dateOfBirth ? new Date(nurseProfile.dateOfBirth).toISOString().split('T')[0] : ''}
              onChange={(e) => handleNurseProfileChange('dateOfBirth', e.target.value)}
              className={`w-full border ${errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm`}
              required
            />
            {errors.dateOfBirth && (
              <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>
            )}
          </div>

          {/* Bank Details */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Bank Account Details
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                 Bank Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nurseProfile.bankDetails?.bankName || ''}
                  onChange={(e) => handleNurseProfileChange('bankDetails.bankName', e.target.value)}
                  className={`w-full border ${errors['bankDetails.bankName'] ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm`}
                  placeholder="Bank Name"
                  required
                />
                {errors['bankDetails.bankName'] && (
                  <p className="mt-1 text-sm text-red-600">{errors['bankDetails.bankName']}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                 BSB Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nurseProfile.bankDetails?.bsbNumber || ''}
                  onChange={(e) => handleNurseProfileChange('bankDetails.bsbNumber', e.target.value)}
                  className={`w-full border ${errors['bankDetails.bsbNumber'] ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm`}
                  placeholder="BSB Number (XXX-XXX)"
                  required
                />
                {errors['bankDetails.bsbNumber'] && (
                  <p className="mt-1 text-sm text-red-600">{errors['bankDetails.bsbNumber']}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                 Account Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nurseProfile.bankDetails?.accountNumber || ''}
                  onChange={(e) => handleNurseProfileChange('bankDetails.accountNumber', e.target.value)}
                  className={`w-full border ${errors['bankDetails.accountNumber'] ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm`}
                  placeholder="Account Number"
                  required
                />
                {errors['bankDetails.accountNumber'] && (
                  <p className="mt-1 text-sm text-red-600">{errors['bankDetails.accountNumber']}</p>
                )}
              </div>
            </div>
          </div>

          {/* ABN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ABN
            </label>
            <input
              type="text"
              value={nurseProfile.abn || ''}
              onChange={(e) => handleNurseProfileChange('abn', e.target.value)}
              className={`w-full border ${errors.abn ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm`}
              placeholder="Enter ABN (optional)"
            />
            {errors.abn && (
              <p className="mt-1 text-sm text-red-600">{errors.abn}</p>
            )}
          </div>

          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name
            </label>
            <input
              type="text"
              value={nurseProfile.companyName || ''}
              onChange={(e) => handleNurseProfileChange('companyName', e.target.value)}
              className={`w-full border ${errors.companyName ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm`}
              placeholder="Enter company name (optional)"
            />
            {errors.companyName && (
              <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
            )}
          </div>
        </div>
      </div>

      {/* Availability Settings */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <img
            src="/images/SVG (9).svg"
            alt="Account Icon"
            className="w-5 h-5 object-contain"
          />
          Availability Settings
        </h2>

                 {loadingAvailability ? (
           <AvailabilitySkeleton />
                  ) : (
            <AvailabilityCalendar 
              availability={{ dateAvailability: dateAvailability }}
              onAvailabilityChange={setAvailability}
              onDateAvailabilityChange={setDateAvailability}
              slotDuration={slotDuration}
              onSlotDurationChange={setSlotDuration}
            />
          )}
      </div>

      {/* Zoom + Session + Notification */}
      {/* <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Zoom Integration
        </h2>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <img
                src="/images/SVG (14).svg"
                alt="Zoom Icon"
                className="w-5 h-5 object-contain"
              />
              <div>
                <h3 className="text-sm font-semibold text-gray-800">
                  Zoom Account
                </h3>
                <p className="text-sm text-gray-500">
                  Connect your Zoom account for video sessions
                </p>
              </div>
            </div>

            <button className="bg-blue-600 text-white px-4 py-1.5 text-sm rounded-md hover:bg-blue-700 transition">
              Connect Zoom Account
            </button>
          </div>

          <div className="flex items-center gap-2 mb-10 ">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-blue-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414L9 13.414l4.707-4.707z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm text-blue-600 font-medium ">
              Not Connected
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-xl font-semibold mb-6">Session Preferences</h2>
        <div className="space-y-6">

          <div>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoJoin}
                onChange={(e) => setAutoJoin(e.target.checked)}
                className="form-checkbox h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-gray-700">Auto-join sessions when they start</span>
            </label>
          </div>

          <div>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notesPopup}
                onChange={(e) => setNotesPopup(e.target.checked)}
                className="form-checkbox h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-gray-700">Show notes popup after each session</span>
            </label>
          </div>

          <div>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={manualApproval}
                onChange={(e) => setManualApproval(e.target.checked)}
                className="form-checkbox h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-gray-700">Require manual approval for session bookings</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buffer Time Between Sessions
            </label>
            <select
              value={bufferTime}
              onChange={(e) => setBufferTime(e.target.value)}
              className="appearance-none w-full border border-gray-300 bg-white rounded-md px-4 py-2 text-sm pr-10 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="5">5 minutes</option>
              <option value="10">10 minutes</option>
              <option value="15">15 minutes</option>
              <option value="20">20 minutes</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-6 shadow-sm">
        <h2 className="text-base font-semibold text-gray-900 mb-4">
          Notifications
        </h2>

        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-medium text-gray-800">
              Email reminders before session
            </p>
            <p className="text-xs text-gray-500">
              Send reminders 1 hour before session
            </p>
          </div>
          <button
            onClick={() => setEmailReminders(!emailReminders)}
            className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors duration-300 ${
              emailReminders ? "bg-blue-500" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform bg-white rounded-full transition-transform duration-300 ${
                emailReminders ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-800">
              Post-session summary emails
            </p>
            <p className="text-xs text-gray-500">
              Receive summary after each session
            </p>
          </div>
          <button
            onClick={() => setPostSessionEmails(!postSessionEmails)}
            className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors duration-300 ${
              postSessionEmails ? "bg-blue-500" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform bg-white rounded-full transition-transform duration-300 ${
                postSessionEmails ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div> */}
      
      {/* Save Button */}
      <div className="flex flex-col gap-4 mt-6">
        {/* Message Display */}
        {saveMessage.text && (
          <div className={`p-3 rounded-md text-sm ${saveMessage.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
            {saveMessage.text}
          </div>
        )}

        <div className="flex items-center justify-between">
          <button className="text-sm text-gray-600 hover:underline">
            Reset to Default
          </button>

          <button
            onClick={saveAvailability}
            disabled={loadingAvailability}
            className={`text-sm font-semibold px-6 py-2 rounded-md transition
              bg-blue-600 text-white hover:bg-blue-700`}
          >
            {savingAvailability ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                </svg>
                Saving...
              </span>
            ) : 'Save All Changes'}
          </button>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                  setPasswordMessage({ type: '', text: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter current password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter new password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm new password"
                />
              </div>

              {passwordMessage.text && (
                <div className={`p-3 rounded-md text-sm ${passwordMessage.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                  {passwordMessage.text}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                    setPasswordMessage({ type: '', text: '' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordChange}
                  disabled={changingPassword}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
